import React, { useEffect, useState } from 'react';
import { API_ENDPOINTS } from '../../utils/endpoints';
import { http_get } from '../../utils/api_client';
import Loader from '../../../organisms/loader/Loader';
import './ResponsesReport.scss';

// BL-114 — the report body for one path (Summary + Individual + CSV export),
// reused by the standalone route and the paths-area Responses page.
const csvEscape = (value) => {
  const s = value == null ? '' : String(value);
  return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
};

const downloadCsv = (name, text) => {
  const blob = new Blob([text], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = name;
  a.click();
  URL.revokeObjectURL(url);
};

const fmtDate = (d) => (d ? new Date(d).toLocaleDateString() : '');

function ResponsesReport({ slug }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('summary');

  useEffect(() => {
    if (!slug) return undefined;
    let alive = true;
    setLoading(true);
    setTab('summary');
    http_get(API_ENDPOINTS.MATERIAL_RESPONSES(slug))
      .then((res) => { if (alive) setData(res.data); })
      .catch(() => { if (alive) setData(null); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [slug]);

  const questions = data?.questions || [];
  const respondents = data?.respondents || [];

  const exportCsv = () => {
    const headers = ['Respondent', 'Email', ...questions.map((q) => q.text), 'Submitted'];
    const rows = respondents.map((r) => [
      r.name,
      r.email || '',
      ...questions.map((q) => (r.answers ? r.answers[q.key] || '' : '')),
      fmtDate(r.submitted_at),
    ]);
    const csv = [headers, ...rows].map((row) => row.map(csvEscape).join(',')).join('\n');
    downloadCsv(`${data?.material?.name || 'responses'}.csv`, csv);
  };

  if (loading) return <div className="rr-panel"><Loader /></div>;
  if (!data) return <div className="rr-panel"><div className="rr-empty">Could not load responses.</div></div>;

  return (
    <div className="rr-panel">
      <div className="rr-head">
        <div>
          <div className="rr-title">{data.material?.name || 'Path'}</div>
          <div className="rr-sub">{data.respondent_count || 0} respondents · {questions.length} questions</div>
        </div>
        <div className="rr-actions">
          <div className="rr-tabs">
            <button type="button" className={tab === 'summary' ? 'on' : ''} onClick={() => setTab('summary')}>Summary</button>
            <button type="button" className={tab === 'individual' ? 'on' : ''} onClick={() => setTab('individual')}>Individual</button>
          </div>
          <button type="button" className="rr-export" onClick={exportCsv} disabled={respondents.length === 0}>Export CSV</button>
        </div>
      </div>

      {respondents.length === 0 ? (
        <div className="rr-empty">No responses captured for this path yet.</div>
      ) : tab === 'summary' ? (
        <div className="rr-summary">
          {questions.map((q) => (
            <div className="rr-q" key={q.key}>
              <div className="rr-q__h">{q.text}</div>
              <div className="rr-q__t">{q.type || 'question'} · {q.response_count} responses</div>
              {q.options ? (
                q.options.map((o) => {
                  const pct = q.response_count ? Math.round((o.count / q.response_count) * 100) : 0;
                  return (
                    <div className="rr-bar" key={o.value}>
                      <span className="lab">{o.value}</span>
                      <span className="track"><span className="fill" style={{ width: `${pct}%` }} /></span>
                      <span className="val">{o.count} · {pct}%</span>
                    </div>
                  );
                })
              ) : (
                (q.text_responses || []).map((t, i) => (
                  // eslint-disable-next-line react/no-array-index-key
                  <div className="rr-txt" key={i}>{t.answer}<span className="who">{t.respondent}</span></div>
                ))
              )}
            </div>
          ))}
        </div>
      ) : (
        <div className="rr-tablewrap">
          <table className="rr-table">
            <thead>
              <tr>
                <th>Respondent</th>
                {questions.map((q) => <th key={q.key}>{q.text}</th>)}
                <th>Submitted</th>
              </tr>
            </thead>
            <tbody>
              {respondents.map((r) => (
                <tr key={r.audience_id}>
                  <td className="who">{r.name}<small>{r.email}</small></td>
                  {questions.map((q) => <td key={q.key}>{r.answers ? r.answers[q.key] : ''}</td>)}
                  <td className="muted">{fmtDate(r.submitted_at)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

export default ResponsesReport;
